// src/hooks/useSSE.js
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEffect, useRef, useCallback, useState } from 'react';
import useAuthStore from '@stores/authStore';

const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_BACKOFF_MS = 30_000;

export default function useSSE(url, { onMessage, onError, enabled = true, public: isPublic = false, eventName } = {}) {
  const controllerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const connect = useCallback(() => {
    if (!enabled) return;

    const token = useAuthStore.getState().token;
    if (!isPublic && !token) return;

    // In dev, use relative path so Vite proxy handles SSE (avoids CORS).
    // In production, use the full VITE_API_BASE_URL.
    const apiBase = import.meta.env.VITE_API_BASE_URL;
    const fullUrl = import.meta.env.DEV && apiBase.includes('localhost')
      ? new URL(apiBase).pathname + url
      : `${apiBase}${url}`;

    // Abort any existing connection
    controllerRef.current?.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;

    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetchEventSource(fullUrl, {
      method: 'GET',
      headers,
      signal: ctrl.signal,
      onopen(response) {
        if (response.ok) {
          setIsConnected(true);
          setError(null);
          reconnectAttempts.current = 0;
        }
      },
      onmessage(event) {
        reconnectAttempts.current = 0;

        // If eventName is set, only process matching events
        if (eventName && event.event !== eventName) return;
        // If no eventName filter, only process unnamed events
        // (matches native EventSource.onmessage behavior)
        if (!eventName && event.event) return;

        try {
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch {
          onMessage(event.data);
        }
      },
      onerror(err) {
        setIsConnected(false);
        setError('Connection lost');

        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            MAX_BACKOFF_MS,
          );
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }

        onError?.();

        // Throw to stop fetchEventSource's built-in retry
        throw err;
      },
    });
  }, [url, onMessage, onError, enabled, isPublic, eventName]);

  useEffect(() => {
    connect();

    return () => {
      controllerRef.current?.abort();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setIsConnected(false);
    };
  }, [connect]);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectAttempts.current = 0;
    setError(null);
    connect();
  }, [connect]);

  return { isConnected, error, reconnect, close: () => controllerRef.current?.abort() };
}
